import Vue from 'vue'
import { ActionTree } from 'vuex'
import * as types from './mutation-types'
import { quickSearchByQuery } from '@vue-storefront/core/lib/search'
import { entityKeyName } from '@vue-storefront/core/store/lib/entities'
import rootStore from '@vue-storefront/core/store'
import i18n from '@vue-storefront/i18n'
import chunk from 'lodash-es/chunk'
import trim from 'lodash-es/trim'
import toString from 'lodash-es/toString'
import { optionLabel } from '../../helpers/optionLabel'
import RootState from '@vue-storefront/core/types/RootState'
import CategoryState from '../../types/CategoryState'
import SearchQuery from '@vue-storefront/core/lib/search/searchQuery'
import { currentStoreView, localizedDispatcherRoute } from '@vue-storefront/core/lib/multistore'
import { Logger } from '@vue-storefront/core/lib/logger'
import { isServer, timezoneTime } from '@vue-storefront/core/helpers'
import config from 'config'
import { processURLAddress } from '@vue-storefront/core/helpers'
import fetch from 'isomorphic-fetch'
import { listCategories, productsByCategory } from '@vue-storefront/core/lib/catalogSearch'

const actions: ActionTree<CategoryState, RootState> = {
  loadDiscountedProducts ({ commit }, { location_code, partner_code, size, from, sort }) {
    let url = `${processURLAddress(config.catalog.discounted_products)}?location_code=${location_code}&partner_code=${partner_code}&size=${size}&from=${from}&sort=${sort}`
    return fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
        .then(resp => resp.json())
        .then(response => {
          if (from === 0) {
            commit(types.CATEGORY_SET_DISCOUNTED_PRODUCTS, { products: response.hits, total: response.total })
          } else {
            commit(types.CATEGORY_ADD_DISCOUNTED_PRODUCTS, response.hits)
          }
          return response
        })
        .catch(error => {
          throw new Error('FetchError in request to ES: ' + error.toString())
        })
  },

  /**
   * Reset current category and path
   * @param {Object} context
   */
  reset (context) {
    context.commit(types.CATEGORY_UPD_CURRENT_CATEGORY_PATH, [])
    context.commit(types.CATEGORY_UPD_CURRENT_CATEGORY, {})
    rootStore.dispatch('stock/clearCache')
    Vue.prototype.$bus.$emit('category-after-reset', { })
  },
  /**
   * Load categories within specified parent
   * @param {Object} commit promise
   * @param {Object} parent parent category
   */
  async list (context, {
    parent = null,
    key = null,
    value = null,
    level = null,
    onlyActive = true,
    onlyNotEmpty = false,
    size = 4000,
    start = 0,
    sort = 'position:asc',
    includeFields = config.entities.optimize ? config.entities.category.includeFields : null,
    excludeFields = config.entities.optimize ? config.entities.category.excludeFields : null,
    skipCache = false,
    updateState = true,
    partner = false
  }) {
    const commit = context.commit
    if (updateState) commit(types.CATEGORY_UPD_CATEGORIES_LOADING, true)

    let customizedQuery = false
    let searchQuery = new SearchQuery()

    if (parent && typeof parent !== 'undefined') {
      searchQuery = searchQuery.applyFilter({
        key: 'parent_id',
        value: { eq: typeof parent === 'object' ? parent.id : parent }
      })
      customizedQuery = true
    }

    if (level !== null) {
      searchQuery = searchQuery.applyFilter({ key: 'level', value: { eq: level } })
      if (level !== config.entities.category.categoriesDynamicPrefetchLevel && !isServer) {
        customizedQuery = true
      }
    }

    if (key !== null) {
      if (Array.isArray(value)) {
        searchQuery = searchQuery.applyFilter({ key, value: { in: value } })
      } else {
        searchQuery = searchQuery.applyFilter({ key, value: { eq: value } })
      }
      customizedQuery = true
    }

    if (onlyActive) {
      searchQuery = searchQuery.applyFilter({ key: 'is_active', value: { eq: true } })
    }

    if (onlyNotEmpty) {
      searchQuery = searchQuery.applyFilter({ key: 'product_count', value: { gt: 0 } })
      customizedQuery = true
    }

    try {
      const shouldRequest =
        skipCache ||
        (!context.state.list || context.state.list.length === 0) ||
        customizedQuery

      let resp

      // C2 remediation: `key` is only ever 'entity_id' among real callers
      // (product/actions.ts, hydrating a product's own category ids), and `onlyActive` is
      // never overridden to false - anything else falls back to the old ES-DSL path, since
      // no real caller needs one today. The new endpoint always applies is_active:true, so
      // it can only stand in when onlyActive is (still) true.
      const canUseSemanticFetch = (key === null || key === 'entity_id') && onlyActive

      if (shouldRequest) {
        resp = canUseSemanticFetch
          ? await listCategories({
            parentId: (parent && typeof parent !== 'undefined') ? (typeof parent === 'object' ? parent.id : parent) : undefined,
            entityIds: key === 'entity_id' ? (Array.isArray(value) ? value : [value]) : undefined,
            level: level !== null ? level : undefined,
            onlyNotEmpty,
            partner
          })
          : await quickSearchByQuery({
            entityType: 'category',
            query: searchQuery,
            sort,
            size,
            start,
            includeFields,
            excludeFields,
            partner
          })

        for (let category of resp.items) {
          if (category.url_path && updateState) {
            await rootStore.dispatch('url/registerMapping', {
              url: localizedDispatcherRoute(category.url_path, currentStoreView().storeCode),
              routeData: {
                params: { slug: category.url_path },
                name: 'category'
              }
            }, { root: true })
          }
        }

        if (updateState) {
          commit(types.CATEGORY_UPD_CATEGORIES, Object.assign(resp, { includeFields, excludeFields }))
          Vue.prototype.$bus.$emit('category-after-list', {
            query: searchQuery,
            sort,
            size,
            start,
            list: resp
          })
        }
      } else {
        resp = {
          items: context.state.list,
          total: context.state.list.length
        }

        if (updateState) {
          Vue.prototype.$bus.$emit('category-after-list', {
            query: searchQuery,
            sort,
            size,
            start,
            list: resp
          })
        }
      }
      return resp
    } finally {
      if (updateState) commit(types.CATEGORY_UPD_CATEGORIES_LOADING, false)
    }
  },

  /**
   * Load category object by specific field - using local storage/indexed Db
   * loadCategories() should be called at first!
   * @param {Object} commit
   * @param {String} key
   * @param {String} value
   * @param {Bool} setCurrentCategory default=true and means that state.current_category is set to the one loaded
   */
  single (context, { key, value, setCurrentCategory = true, setCurrentCategoryPath = true, populateRequestCacheTags = true, skipCache = false, partner = false, updateState = true }) {
    const state = context.state
    const commit = context.commit
    const dispatch = context.dispatch

    return new Promise((resolve, reject) => {
      const fetchCat = ({ key, value }) => {
        context.dispatch('list', { key, value, partner, updateState }).then(res => {
          if (res && res.items && res.items.length) {
            setcat(null, res.items[0]) // eslint-disable-line @typescript-eslint/no-use-before-define
          } else {
            reject(new Error('Category query returned empty result ' + key + ' = ' + value))
          }
        }).catch(reject)
      }
      let setcat = (error, mainCategory) => {
        if (!mainCategory) {
          fetchCat({ key, value })
          return
        }
        if (error) {
          Logger.error(error)()
          reject(error)
        }

        if (setCurrentCategory) {
          commit(types.CATEGORY_UPD_CURRENT_CATEGORY, mainCategory)
        }
        if (populateRequestCacheTags && mainCategory && Vue.prototype.$cacheTags) {
          Vue.prototype.$cacheTags.add(`C${mainCategory.entity_id}`)
        }
        if (setCurrentCategoryPath) {
          let currentPath = []
          let recurCatFinder = (category) => {
            if (!category) {
              return
            }
            if (category.parent_id >= config.entities.category.categoriesRootCategorylId) {
              dispatch('single', { key: 'entity_id', value: category.parent_id, setCurrentCategory: false, setCurrentCategoryPath: false }).then((sc) => { // TODO: move it to the server side for one requests OR cache in indexedDb
                if (!sc) {
                  commit(types.CATEGORY_UPD_CURRENT_CATEGORY_PATH, currentPath)
                  Vue.prototype.$bus.$emit('category-after-single', { category: mainCategory })
                  return resolve(mainCategory)
                }
                currentPath.unshift(sc)
                if (sc.parent_id) {
                  recurCatFinder(sc)
                }
              }).catch(err => {
                Logger.error(err)()
                commit(types.CATEGORY_UPD_CURRENT_CATEGORY_PATH, currentPath) // this is the case when category is not binded to the root tree - for example 'Erin Recommends'
                resolve(mainCategory)
              })
            } else {
              commit(types.CATEGORY_UPD_CURRENT_CATEGORY_PATH, currentPath)
              Vue.prototype.$bus.$emit('category-after-single', { category: mainCategory })
              resolve(mainCategory)
            }
          }
          if (typeof mainCategory !== 'undefined') {
            recurCatFinder(mainCategory) // TODO: Store breadcrumbs in IndexedDb for further usage to optimize speed?
          } else {
            reject(new Error('Category query returned empty result ' + key + ' = ' + value))
          }
        } else {
          Vue.prototype.$bus.$emit('category-after-single', { category: mainCategory })
          resolve(mainCategory)
        }
      }

      let foundInLocalCache = false
      if (state.list.length > 0 && !skipCache) { // SSR - there were some issues with using localForage, so it's the reason to use local state instead, when possible
        let category = state.list.find((itm) => { return itm[key] === value })
        // Check if category exists in the store OR we have recursively reached Default category (id=1)
        if (category && value >= config.entities.category.categoriesRootCategorylId/** root category parent */) {
          foundInLocalCache = true
          setcat(null, category)
        }
      }
      if (!foundInLocalCache) {
        if (skipCache || isServer) {
          fetchCat({ key, value })
        } else {
          const catCollection = Vue.prototype.$db.categoriesCollection
          // Check if category does not exist in the store AND we haven't recursively reached Default category (id=1)
          catCollection.getItem(entityKeyName(key, value), setcat)
        }
      }
    })
  },
  /**
   * Filter category products
   */
  products (context, { populateAggregations = false, filters = [], searchProductQuery, current = 0, perPage = 50, sort = '', includeFields = null, excludeFields = null, configuration = null, append = false, skipCache = false, cacheOnly = false, partner = false }) {
    context.dispatch('setSearchOptions', {
      populateAggregations,
      filters,
      current,
      perPage,
      includeFields,
      excludeFields,
      configuration,
      append,
      sort,
      partner
    })

    let prefetchGroupProducts = true
    if (config.entities.twoStageCaching && config.entities.optimize && !isServer && !rootStore.state.twoStageCachingDisabled) { // only client side, only when two stage caching enabled
      includeFields = config.entities.productListWithChildren.includeFields // we need configurable_children for filters to work
      excludeFields = config.entities.productListWithChildren.excludeFields
      prefetchGroupProducts = false
      Logger.log('Using two stage caching for performance optimization - executing first stage product pre-fetching')()
    } else {
      prefetchGroupProducts = true
      if (rootStore.state.twoStageCachingDisabled) {
        Logger.log('Two stage caching is disabled runtime because of no performance gain')()
      } else {
        Logger.log('Two stage caching is disabled by the config')()
      }
    }
    if (cacheOnly) {
      excludeFields = null
      includeFields = null
      Logger.log('Caching request only, no state update')()
    }
    let t0 = timezoneTime().getTime()

    const precachedQuery = searchProductQuery
    let productPromise = rootStore.dispatch('product/list', {
      query: precachedQuery,
      semanticFetch: () => productsByCategory({
        searchQuery: precachedQuery, start: current, size: perPage, sort, includeFields, excludeFields, partner
      }),
      start: current,
      size: perPage,
      excludeFields: excludeFields,
      includeFields: includeFields,
      configuration: configuration,
      append: append,
      sort: sort,
      updateState: !cacheOnly,
      prefetchGroupProducts: prefetchGroupProducts,
      partner: partner
    }).then((res) => {
      let t1 = timezoneTime().getTime()
      rootStore.state.twoStageCachingDelta1 = t1 - t0

      let subloaders = []
      if (!res || (res.noresults)) {
        rootStore.dispatch('notification/spawnNotification', {
          type: 'warning',
          message: i18n.t('No products synchronized for this category. Please come back while online!'),
          action1: { label: i18n.t('OK') }
        })
        if (!append) rootStore.dispatch('product/reset')
        rootStore.state.product.list = { items: [] } // no products to show TODO: refactor to rootStore.state.category.reset() and rootStore.state.product.reset()
        // rootStore.state.category.filters = { color: [], size: [], price: [] }
        return []
      } else {
        if (config.products.filterUnavailableVariants && config.products.configurableChildrenStockPrefetchStatic) { // prefetch the stock items
          const skus = []
          let prefetchIndex = 0
          res.items.map(i => {
            if (config.products.configurableChildrenStockPrefetchStaticPrefetchCount > 0) {
              if (prefetchIndex > config.products.configurableChildrenStockPrefetchStaticPrefetchCount) return
            }
            skus.push(i.sku) // main product sku to be checked anyway
            if (i.type_id === 'configurable' && i.configurable_children && i.configurable_children.length > 0) {
              for (const confChild of i.configurable_children) {
                const cachedItem = context.rootState.stock.cache[confChild.id]
                if (typeof cachedItem === 'undefined' || cachedItem === null) {
                  skus.push(confChild.sku)
                }
              }
              prefetchIndex++
            }
          })
          for (const chunkItem of chunk(skus, 15)) {
            rootStore.dispatch('stock/list', { skus: chunkItem, skipCache }) // store it in the cache
          }
        }
        if (populateAggregations === true && res.aggregations) { // populate filter aggregates
          for (let attrToFilter of filters) { // fill out the filter options
            let filterOptions = []

            let uniqueFilterValues = new Set<string>()
            if (attrToFilter !== 'price') {
              if (res.aggregations['agg_terms_' + attrToFilter]) {
                let buckets = res.aggregations['agg_terms_' + attrToFilter].buckets
                if (res.aggregations['agg_terms_' + attrToFilter + '_options']) {
                  buckets = buckets.concat(res.aggregations['agg_terms_' + attrToFilter + '_options'].buckets)
                }

                for (let option of buckets) {
                  uniqueFilterValues.add(toString(option.key))
                }
              }

              uniqueFilterValues.forEach(key => {
                const label = optionLabel(rootStore.state.attribute, { attributeKey: attrToFilter, optionId: key })
                if (trim(label) !== '') { // is there any situation when label could be empty and we should still support it?
                  filterOptions.push({
                    id: key,
                    label: label
                  })
                }
              });
            } else { // special case is range filter for prices
              const storeView = currentStoreView()
              const currencySign = storeView.i18n.currencySign
              if (res.aggregations['agg_range_' + attrToFilter]) {
                let index = 0
                let count = res.aggregations['agg_range_' + attrToFilter].buckets.length
                for (let option of res.aggregations['agg_range_' + attrToFilter].buckets) {
                  filterOptions.push({
                    id: option.key,
                    from: option.from,
                    to: option.to,
                    label: (index === 0 || (index === count - 1)) ? (option.to ? '< ' + currencySign + option.to : '> ' + currencySign + option.from) : currencySign + option.from + (option.to ? ' - ' + option.to : '')// TODO: add better way for formatting, extract currency sign
                  })
                  index++
                }
              }
            }
            context.dispatch('addAvailableFilter', {
              key: attrToFilter,
              options: filterOptions
            })
          }
        }
      }
      return subloaders
    }).catch((err) => {
      Logger.error(err)()
      rootStore.dispatch('notification/spawnNotification', {
        type: 'warning',
        message: i18n.t('No products synchronized for this category. Please come back while online!'),
        action1: { label: i18n.t('OK') }
      })
    })

    if (config.entities.twoStageCaching && config.entities.optimize && !isServer && !rootStore.state.twoStageCachingDisabled && !cacheOnly) { // second stage - request for caching entities; if cacheOnly set - the caching took place with the stage1 request!
      Logger.log('Using two stage caching for performance optimization - executing second stage product caching', 'category') // TODO: in this case we can pre-fetch products in advance getting more products than set by pageSize()
      rootStore.dispatch('product/list', {
        query: precachedQuery,
        semanticFetch: () => productsByCategory({
          searchQuery: precachedQuery, start: current, size: perPage, sort, includeFields: null, excludeFields: null, partner
        }),
        start: current,
        size: perPage,
        excludeFields: null,
        includeFields: null,
        configuration: configuration,
        sort: sort,
        updateState: false, // not update the product listing - this request is only for caching
        prefetchGroupProducts: prefetchGroupProducts
      }).catch((err) => {
        Logger.info("Problem with second stage caching - couldn't store the data", 'category')()
        Logger.info(err, 'category')()
      }).then((res) => {
        let t2 = timezoneTime().getTime()
        rootStore.state.twoStageCachingDelta2 = t2 - t0
        Logger.log('Using two stage caching for performance optimization - Time comparison stage1 vs stage2' + rootStore.state.twoStageCachingDelta1 + rootStore.state.twoStageCachingDelta2, 'category')()
        if (rootStore.state.twoStageCachingDelta1 > rootStore.state.twoStageCachingDelta2) { // two stage caching is not making any good
          rootStore.state.twoStageCachingDisabled = true
          Logger.log('Disabling two stage caching', 'category')()
        }
      })
    }
    return productPromise
  },
  addAvailableFilter ({commit}, {key, options} = {}) {
    if (key) commit(types.CATEGORY_ADD_AVAILABLE_FILTER, {key, options})
  },
  resetFilters (context) {
    context.commit(types.CATEGORY_REMOVE_FILTERS)
  },
  searchProductQuery (context, productQuery) {
    context.commit(types.CATEGORY_UPD_SEARCH_PRODUCT_QUERY, productQuery)
  },
  setSearchOptions ({commit}, searchOptions) {
    commit(types.CATEGORY_SET_SEARCH_OPTIONS, searchOptions)
  },
  mergeSearchOptions ({commit}, searchOptions) {
    commit(types.CATEGORY_MERGE_SEARCH_OPTIONS, searchOptions)
  },
  cleanUpCategory ({commit}) {
    commit('cleanUpCategory')
  },
  async loadCategoryTree ({ getters, commit }, { partner_code, location_code }) {
    let partnerKey = `${location_code}_${partner_code}`
    if (getters.getPartnerCategoryTreeLoading) {
      return
    }
    commit(types.CATEGORY_SET_CATALOG_TREE_LOADING, true)
    // K2-2976 Phase 4: partner_code and location_code sent separately, not as the combined
    // partnerKey - the API side needs them apart (city index name + a partner_code filter),
    // and partner codes containing underscores (e.g. "al_forno") make splitting the combined
    // key back apart unreliable.
    let url = processURLAddress(`${config.catalog.tree}?partner_code=${partner_code}&location_code=${location_code}`)
    await fetch(url, {
      method: config.elasticsearch.queryMethod,
      mode: 'cors',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
      .then(resp => resp.json())
      .then(categoryTree => {
        commit(types.CATEGORY_SET_CATALOG_TREE, { partnerKey, categoryTree })
        return categoryTree
      })
      .catch(error => {
        throw new Error('FetchError in request to ES: ' + error.toString())
      })
      .finally(() => {
        commit(types.CATEGORY_SET_CATALOG_TREE_LOADING, false)
      })
  },
  getPartnerCategoryTree({ getters, dispatch }, { partner_code, location_code }) {
    let partnerKey = `${location_code}_${partner_code}`
    return getters.getPartnerCategoryTree(partnerKey)
      ? getters.getPartnerCategoryTree(partnerKey)
      : dispatch('loadCategoryTree', { partner_code, location_code })
  },
  resetList ({ commit }) {
    commit(types.CATEGORY_RESET_CATEGORIES)
  }
}

export default actions
